import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form, ListGroup, Badge } from "react-bootstrap";
import { useLoader } from '../../_helper/LoaderContext';
import { useLab } from '../../_helper/PharmacyProvider';
import { formatDoctorDisplayDateTime } from '../../utils/dateTime/dateFormat';
import { reportList, sendEmail, sendWhatsApp } from './service/report-test-service';
import { toast } from 'react-toastify';
import S3ImageLoader from '../../S3ImageLoader';
import FileLink from '../../FileLink';
import { useLicense } from '../../_helper/LicenseContext';
import PrintReport from './PrintReport';
import { Printer } from 'lucide-react';

export default function ReportList({ setUploadModelOpen, toggleUploadModel, data }) {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const { currentLab } = useLab();
    const [reports, setReports] = useState([]);
    const [selectedReports, setSelectedReports] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const { decodeTokenOnly } = useLicense()
    const [whastappAllow, setWhatsappAllow] = useState(false)
    const [emailAllow, setEmailAllow] = useState(false);
    const [print, setPrint] = useState(false);
    const [imageUrl, setImageUrl] = useState("")

    useEffect(() => {
        decodeTokenOnly()
            .then((decoded) => {
                // console.log(decoded)
                if (decoded.features.whatsapp_reports == true) {
                    setWhatsappAllow(true);
                } else {
                    setWhatsappAllow();
                }
                if (decoded.features.email_reports == true) {
                    setEmailAllow(true);
                } else {
                    setEmailAllow();
                }
            })
            .catch(() => {
                setWhatsappAllow();
                setEmailAllow()
            });
    }, [])

    useEffect(() => {
        if (data?._id) {
            getReportList();
        }
    }, [data]);

    const getReportList = async () => {
        setLoading(true);
        try {
            const payload = { testOrderId: data._id };
            const res = await reportList(payload);
            // console.log(res)
            if (res.data.success == true) {
                setReports(res.data.data || []);
            } else {
                setReports([]);
            }
        } catch (error) {
            console.error("Failed to load reports:", error);
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    // Toggle single checkbox
    const handleCheckboxChange = (reportId) => {
        setSelectedReports(prev =>
            prev.includes(reportId)
                ? prev.filter(id => id !== reportId)
                : [...prev, reportId]
        );
    };

    // Toggle Select All
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedReports([]);
        } else {
            setSelectedReports(reports.map(r => r._id));
        }
        setSelectAll(!selectAll);
    };

    // Send Button - Console log selected IDs
    const handleSend = async () => {
        if (selectedReports.length === 0) {
            alert(t("Please select at least one report!"));
            return;
        }
        const payload = {
            testReportIds: selectedReports
        }
        setLoading(true)
        try {
            const res = await sendEmail(payload);
            if (res.data.success == true) {
                toast.success(res.data.message);
                toggleUploadModel()
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    };

    const handleWhatsapp = async () => {
        if (selectedReports.length === 0) {
            alert(t("Please select at least one report!"));
            return;
        }
        const payload = {
            testReportIds: selectedReports,
            message: "Lab Test Report"
        }
        setLoading(true)
        try {
            const res = await sendWhatsApp(payload);
            if (res.data.success == true) {
                toast.success(res.data.message);
                toggleUploadModel()
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    }

    const openModelForPrint = (image) => {
        setPrint(true)
        setImageUrl(image);
        console.log(image)
    }

    return (
        <>
            <Modal.Header closeButton className="tw-py-2.5 tw-px-4">
                <Modal.Title className="tw-text-base tw-font-bold">{t("Report List")}</Modal.Title>
            </Modal.Header>

            <Modal.Body className="tw-py-3 tw-px-4">
                {/* Body */}
                <div className="tw-flex-1 tw-overflow-y-auto">
                    {reports.length === 0 ? (
                        <div className="tw-text-center tw-py-8">
                            <div className="tw-bg-gray-200 tw-border-2 tw-border-dashed tw-rounded-xl tw-w-16 tw-h-16 tw-mx-auto tw-mb-2.5 tw-flex tw-items-center tw-justify-center">
                                <i className="fa fa-file-pdf-o tw-text-2xl tw-text-gray-400"></i>
                            </div>
                            <p className="tw-text-gray-500 tw-text-sm">{t("No reports generated yet")}</p>
                        </div>
                    ) : (
                        <>
                            {/* Select All */}
                            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2.5 tw-py-2 tw-px-3 tw-bg-gray-50 tw-rounded-lg">
                                <label className="tw-flex tw-items-center tw-gap-2.5 tw-cursor-pointer tw-mb-0">
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                        className="tw-w-4 tw-h-4 tw-rounded tw-border tw-border-gray-300 
                                                   checked:tw-bg-[#025720] checked:tw-border-[#025720] 
                                                   focus:tw-ring-2 focus:tw-ring-[#025720]/30 
                                                   focus:tw-outline-none tw-transition-all tw-duration-200 tw-cursor-pointer"
                                    />
                                    <span className="tw-font-semibold tw-text-xs tw-text-gray-700">{t("Select All")}</span>
                                </label>
                                <span className="tw-text-xs tw-text-gray-500">
                                    {selectedReports.length} {t("of")} {reports.length} {t("selected")}
                                </span>
                            </div>

                            {/* Reports List */}
                            <div className="tw-space-y-2">
                                {reports.map((report) => {
                                    const isSelected = selectedReports.includes(report._id);

                                    return (
                                        <div
                                            key={report._id}
                                            className={`tw-border tw-rounded-lg tw-p-2.5 tw-transition-all tw-duration-200 ${isSelected
                                                ? "tw-border-[#025720] tw-bg-green-50/60 tw-shadow-sm"
                                                : "tw-border-gray-200 hover:tw-border-gray-300"
                                                }`}
                                        >
                                            <div className="tw-flex tw-items-center tw-gap-3">
                                                {/* Custom Tailwind Checkbox */}
                                                <label className="tw-flex tw-items-center tw-cursor-pointer tw-mb-0 tw-shrink-0">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleCheckboxChange(report._id)}
                                                        className="tw-w-4 tw-h-4 tw-rounded tw-border tw-border-gray-300 
                                                                   checked:tw-bg-[#025720] checked:tw-border-[#025720] 
                                                                   focus:tw-ring-2 focus:tw-ring-[#025720]/30 
                                                                   focus:tw-outline-none tw-transition-all tw-duration-200 tw-cursor-pointer"
                                                    />
                                                </label>

                                                <div className="tw-flex-1 tw-min-w-0">
                                                    <h4 className="tw-font-bold tw-text-sm tw-text-gray-800 tw-truncate tw-mb-0.5">
                                                        {report.testname || report.reportKey}
                                                    </h4>
                                                    <div className="tw-text-xs tw-text-gray-600 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-0.5">
                                                        <div>
                                                            <span className="tw-font-medium">{t("Report ID")}:</span>{' '}
                                                            <code className="tw-bg-gray-200 tw-text-gray-700 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[11px]">
                                                                {report.reportId}
                                                            </code>
                                                        </div>
                                                        <div>
                                                            <span className="tw-font-medium">{t("Generated")}:</span>{' '}
                                                            <span>{formatDoctorDisplayDateTime(report.createdAt, currentLab) || "-"}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="tw-flex tw-flex-col tw-items-end tw-gap-1 tw-shrink-0">
                                                    {/* {report.reportPdfPath && ( */}
                                                    <FileLink
                                                        fileName={report?.reportPdfPath}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{ textDecoration: "none" }}
                                                        className='tw-text-black hover:tw-text-white tw-rounded-lg px-2 py-0.5 hover:tw-bg-[#1b255b] tw-text-xs tw-font-medium tw-inline-flex tw-items-center'
                                                    >
                                                        {t("View Report")}
                                                    </FileLink>
                                                    {/* )} */}

                                                    <span
                                                        onClick={() => openModelForPrint(report?.reportPdfPath)}
                                                        className='tw-text-black hover:tw-text-white tw-rounded-lg px-2 py-0.5 hover:tw-bg-[#1b255b] tw-cursor-pointer tw-text-xs tw-font-medium tw-inline-flex tw-items-center tw-gap-1'
                                                        style={{ border: '1px solid #1b255b' }}
                                                    >
                                                        <Printer className='tw-w-3.5 tw-h-3.5' /> {t("Print Report")}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

            </Modal.Body>

            <Modal.Footer style={{ display: "block" }} className='text-center tw-py-2.5 tw-px-4'>
                <div className="text-center tw-mb-2">
                    <div className="tw-text-sm tw-font-bold tw-text-gray-700">{t("Send To")}</div>
                </div>
                <div className="tw-flex tw-justify-center tw-items-center tw-gap-8">
                    {whastappAllow == true && (
                        <div
                            className="text-success tw-cursor-pointer text-center tw-flex tw-flex-col tw-items-center tw-transition-transform hover:tw-scale-105"
                            onClick={handleWhatsapp}
                        >
                            <i className="bi bi-whatsapp tw-text-2xl"></i>
                            <div className="tw-text-xs tw-font-bold tw-mt-1">{t("WhatsApp")}</div>
                        </div>
                    )}
                    {emailAllow == true && (
                        <div
                            className="text-center tw-text-cyan-500 tw-cursor-pointer tw-flex tw-flex-col tw-items-center tw-transition-transform hover:tw-scale-105"
                            onClick={handleSend}
                        >
                            <i className="bi bi-envelope-arrow-up-fill tw-text-2xl"></i>
                            <div className="tw-text-xs tw-font-bold tw-mt-1">{t("Email")}</div>
                        </div>
                    )}
                </div>
            </Modal.Footer>

            <PrintReport print={print} setPrint={setPrint} imageUrl={imageUrl} />
        </>
    );
}